/**
 * ConditionRow — one comparison/existence row in the visual builder's
 * condition stack. The field label is immutable (drop a new field to swap),
 * the operator and value controls are typed by the field descriptor.
 *
 * Strings → text input.
 * Numbers/integers → number input.
 * Y/N → Yes/No select.
 * Enum → dropdown of allowed values.
 * Date / datetime → native picker.
 */

import { useMemo } from 'react';
import type { ConditionLiteral, ConditionOperator } from '@mana/shared';
import {
  describeConditionInEnglish,
  operatorsForType,
  type LoanFieldDescriptor,
} from '@mana/shared';
import type { VisualConditionRow } from './serialize.js';
import { lookupField, lookupFieldByPath } from './serialize.js';

export interface ConditionRowProps {
  row: VisualConditionRow;
  readOnly: boolean;
  onChange: (next: VisualConditionRow) => void;
  onRemove: () => void;
}

export function ConditionRow({ row, readOnly, onChange, onRemove }: ConditionRowProps) {
  const field = useMemo<LoanFieldDescriptor | undefined>(() => {
    const byId = lookupField(row.fieldId);
    if (byId) return byId;
    return lookupFieldByPath(row.path);
  }, [row.fieldId, row.path]);

  const allowedOps: readonly ConditionOperator[] = field
    ? operatorsForType(field.type)
    : (['eq', 'neq', 'exists', 'not_exists'] as const);

  const isExistence = row.op === 'exists' || row.op === 'not_exists';

  const englishPreview = useMemo(() => {
    // Best-effort preview when the row is fully formed.
    try {
      if (isExistence) {
        return describeConditionInEnglish({
          op: row.op as 'exists' | 'not_exists',
          path: row.path,
        });
      }
      if (row.value === undefined) return null;
      const v = row.value;
      return describeConditionInEnglish({
        op: row.op as 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte',
        path: row.path,
        value: v,
      });
    } catch {
      return null;
    }
  }, [row, isExistence]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {/* Field chip — immutable -------------------------------------------------- */}
        <span
          className="inline-flex items-center gap-1 rounded-full bg-brand-navy-50 px-2 py-1 text-[11px] font-medium text-brand-navy-800 ring-1 ring-brand-navy-200"
          title={field?.description ?? row.path}
        >
          {field?.label ?? humanizePath(row.path)}
        </span>

        {/* Operator -------------------------------------------------------------- */}
        <select
          value={row.op}
          onChange={(e) => {
            const op = e.target.value as ConditionOperator;
            if (op === 'exists' || op === 'not_exists') {
              onChange({ ...row, op, value: undefined });
              return;
            }
            // Coerce default value to the new op's expected type.
            let nextValue: ConditionLiteral | undefined = row.value;
            if (op === 'gt' || op === 'gte' || op === 'lt' || op === 'lte') {
              const n = typeof row.value === 'number' ? row.value : Number(row.value ?? 0);
              nextValue = Number.isFinite(n) ? n : 0;
            } else if (row.value === undefined) {
              nextValue = field?.type === 'boolean_yn' ? 'Y' : '';
            }
            onChange({ ...row, op, value: nextValue });
          }}
          disabled={readOnly}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200 disabled:bg-slate-50"
        >
          {allowedOps.map((op) => (
            <option key={op} value={op}>
              {operatorLabel(op, field?.type)}
            </option>
          ))}
        </select>

        {/* Value control --------------------------------------------------------- */}
        {!isExistence && (
          <ValueControl
            field={field}
            value={row.value}
            disabled={readOnly}
            onChange={(v) => onChange({ ...row, value: v })}
          />
        )}

        <div className="ml-auto">
          <button
            type="button"
            disabled={readOnly}
            onClick={onRemove}
            title="Remove condition"
            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ✕
          </button>
        </div>
      </div>
      {englishPreview && (
        <p className="mt-1.5 text-[10px] italic text-slate-500">
          {englishPreview}
        </p>
      )}
      {field?.source === 'manual' && (
        <p className="mt-1.5 rounded bg-amber-50 px-2 py-1 text-[10px] text-amber-800 ring-1 ring-amber-200">
          This value is captured manually on the Pipeline drawer — make sure
          it&apos;s filled before the workflow runs.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ValueControl — typed by field.type
// ---------------------------------------------------------------------------

function ValueControl({
  field,
  value,
  disabled,
  onChange,
}: {
  field: LoanFieldDescriptor | undefined;
  value: ConditionLiteral | undefined;
  disabled: boolean;
  onChange: (next: ConditionLiteral) => void;
}) {
  const valueStr = value === undefined || value === null ? '' : String(value);
  const baseInput =
    'rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200 disabled:bg-slate-50';

  if (field?.type === 'boolean_yn') {
    return (
      <select
        value={valueStr}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={baseInput}
      >
        <option value="Y">Yes</option>
        <option value="N">No</option>
      </select>
    );
  }

  if (field?.type === 'enum' && field.enumValues) {
    return (
      <select
        value={valueStr}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={baseInput}
      >
        {field.enumValues.map((v) => (
          <option key={v.value} value={v.value}>
            {v.label}
          </option>
        ))}
      </select>
    );
  }

  if (field?.type === 'number' || field?.type === 'integer') {
    return (
      <input
        type="number"
        value={valueStr}
        placeholder={field.exampleValue}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(0);
            return;
          }
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : 0);
        }}
        className={`${baseInput} w-24`}
      />
    );
  }

  if (field?.type === 'date') {
    return (
      <input
        type="date"
        value={valueStr}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`${baseInput} w-40`}
      />
    );
  }

  if (field?.type === 'datetime') {
    return (
      <input
        type="datetime-local"
        value={valueStr}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`${baseInput} w-52`}
      />
    );
  }

  // Default: string.
  return (
    <input
      type="text"
      value={valueStr}
      placeholder={field?.exampleValue ?? 'value'}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`${baseInput} w-40`}
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function operatorLabel(op: ConditionOperator, type: LoanFieldDescriptor['type'] | undefined): string {
  const isDateLike = type === 'date' || type === 'datetime';
  switch (op) {
    case 'eq':
      return 'is';
    case 'neq':
      return 'is not';
    case 'gt':
      return isDateLike ? 'is after' : 'is greater than';
    case 'gte':
      return isDateLike ? 'on or after' : 'is at least';
    case 'lt':
      return isDateLike ? 'is before' : 'is less than';
    case 'lte':
      return isDateLike ? 'on or before' : 'is at most';
    case 'exists':
      return 'is set';
    case 'not_exists':
      return 'is not set';
  }
}

function humanizePath(path: string): string {
  const last = path.split('.').pop() ?? path;
  const spaced = last.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
